import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useAdminStore } from '../admin/store/adminStore';
import { formatPrice } from '../data/products';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../admin/types/admin';
import {
  User, Mail, Phone, MapPin, Lock, Save, Package, LogOut, ChevronDown, ChevronUp, Eye
} from 'lucide-react';

const AccountPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, updateProfile } = useAuthStore();
  const { orders } = useAdminStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'orders'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState<string | null>(null);
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    district: user?.district || '',
    postalCode: user?.postalCode || '',
  });

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <p className="text-gray-500 text-lg mb-4">Bu sayfayı görüntülemek için giriş yapmalısınız</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#E31E24] text-white font-bold rounded-xl hover:bg-[#c41a1f] transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  const userOrders = orders.filter(o =>
    o.customerEmail.toLowerCase() === user.email.toLowerCase()
  );

  const handleSave = () => {
    updateProfile(formData);
    setIsEditing(false);
    setSuccess('Profil bilgileriniz güncellendi');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a2e] text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black">👤 Hesabım</h1>
          <p className="text-gray-400 mt-2">Hoş geldiniz, {user.firstName} {user.lastName}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-green-700 font-medium">{success}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'profile'
                ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profil Bilgileri
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
              activeTab === 'orders'
                ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            Siparişlerim ({userOrders.length})
          </button>
        </div>

        {activeTab === 'profile' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-800">Kişisel Bilgiler</h2>
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#E31E24] rounded-xl hover:bg-[#c41a1f] transition-colors flex items-center gap-1"
                    >
                      <Save className="w-4 h-4" /> Kaydet
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 text-sm font-medium text-[#E31E24] bg-[#E31E24]/10 rounded-xl hover:bg-[#E31E24]/20 transition-colors"
                  >
                    Düzenle
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">E-posta</label>
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {user.email}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Ad</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                ) : (
                  <div className="px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">{user.firstName}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Soyad</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                ) : (
                  <div className="px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">{user.lastName}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Telefon</label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {user.phone}
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Adres</label>
                {isEditing ? (
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm resize-none"
                  />
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">
                    <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                    {user.address || 'Belirtilmemiş'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">İl</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                ) : (
                  <div className="px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">{user.city || '-'}</div>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">İlçe</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                ) : (
                  <div className="px-3 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700">{user.district || '-'}</div>
                )}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Çıkış Yap
              </button>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-4">
            {userOrders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                <div className="text-5xl mb-4">📦</div>
                <p className="text-gray-500 text-lg mb-2">Henüz siparişiniz yok</p>
                <button
                  onClick={() => navigate('/magaza')}
                  className="px-6 py-3 bg-[#E31E24] text-white font-bold rounded-xl hover:bg-[#c41a1f] transition-colors text-sm"
                >
                  Alışverişe Başla
                </button>
              </div>
            ) : (
              userOrders.map((order) => (
                <div key={order.id} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-bold text-gray-800">Sipariş: {order.orderNumber}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(order.createdAt).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ORDER_STATUS_COLORS[order.status]}`}>
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                      <button
                        onClick={() => setShowOrderDetail(showOrderDetail === order.id ? null : order.id)}
                        className="p-2 text-gray-400 hover:text-[#E31E24] transition-colors"
                      >
                        {showOrderDetail === order.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {showOrderDetail === order.id && (
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400 text-xs">Toplam Tutar</p>
                          <p className="font-bold text-[#E31E24]">{formatPrice(order.totalAmount)} ₺</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Kargo</p>
                          <p className="font-semibold">{order.shippingCost === 0 ? 'Ücretsiz' : `${formatPrice(order.shippingCost)} ₺`}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-gray-400 text-xs mb-2">Sipariş Edilen Ürünler</p>
                        <div className="space-y-2">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                              <img src={item.product.images[0]} alt={item.product.name} className="w-10 h-10 object-contain bg-white rounded-lg" />
                              <div className="flex-1">
                                <p className="text-xs font-semibold text-gray-800 truncate">{item.product.name}</p>
                                <p className="text-xs text-gray-400">{item.quantity} adet</p>
                              </div>
                              <span className="text-xs font-bold">{formatPrice((item.product.salePrice > 0 ? item.product.salePrice : item.product.regularPrice) * item.quantity)} ₺</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-gray-400 text-xs mb-1">Teslimat Adresi</p>
                        <p className="text-sm text-gray-700">{order.shippingAddress}, {order.district}, {order.city}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
